import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import PayRollLayoutWrapper from "../components/PayRollLayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Save, RefreshCcw, Calendar } from "lucide-react";

const BASE_URL = "http://nissi-india.com";

function useQuery() {
  return useMemo(() => new URLSearchParams(window.location.search), []);
}

const LicenseDateUpdate = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const query = useQuery();
  const employeeId = Number(query.get("alertId") || 0);

  const [loading, setLoading] = useState(false);

  const [employeeCode, setEmployeeCode] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [designationName, setDesignationName] = useState("");
  const [branchName, setBranchName] = useState("");

  // ✅ input type="date" value (YYYY-MM-DD)
  const [licenseDateISO, setLicenseDateISO] = useState("");

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const fetchDetails = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!employeeId) {
      toast.error("Missing alertId/employeeId in URL.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/license-date-update/details`, {
        params: { finYearId: Number(finYearId), employeeId },
      });

      if (res?.data?.success && res?.data?.row) {
        const r = res.data.row;
        setEmployeeCode(r.employeeCode || "");
        setEmployeeName(r.employeeName || "");
        setDesignationName(r.designationName || "");
        setBranchName(r.branchName || "");
        setLicenseDateISO(r.licenseDateISO || "");
      } else {
        toast.warn(res?.data?.message || "No record found.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!employeeId) {
      toast.error("Missing employeeId.");
      return;
    }

    // allow empty -> null
    if (licenseDateISO && !/^\d{4}-\d{2}-\d{2}$/.test(licenseDateISO)) {
      toast.warn("Invalid date format.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.put(
        `${BASE_URL}/api/license-date-update/update`,
        { employeeId, licenseDate: licenseDateISO },
        { params: { finYearId: Number(finYearId) } }
      );

      if (res?.data?.success) {
        window.alert(res?.data?.message || "License Date Updated Successfully..");

        // ✅ Refresh parent like ASP
        try {
          if (window.opener && !window.opener.closed) {
            // if parent is React route, reload is simplest
            window.opener.location.reload();
          }
        } catch (_) {}

        window.close();
      } else {
        toast.error(res?.data?.message || "Update failed.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl">UPDATE LICENSE DATE</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-2xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          <div className={cardBase + " p-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelBase}>Employee Code</label>
                <div className={`mt-2 ${inputBase}`}>{employeeCode || "-"}</div>
              </div>

              <div>
                <label className={labelBase}>Employee Name</label>
                <div className={`mt-2 ${inputBase}`}>{employeeName || "-"}</div>
              </div>

              <div>
                <label className={labelBase}>Designation</label>
                <div className={`mt-2 ${inputBase}`}>{designationName || "-"}</div>
              </div>

              <div>
                <label className={labelBase}>Branch</label>
                <div className={`mt-2 ${inputBase}`}>{branchName || "-"}</div>
              </div>

              <div className="md:col-span-2">
                <label className={labelBase}>
                  License Date <span className="text-red-500">*</span>
                </label>
                <div className="mt-2 flex items-center gap-2">
                  <Calendar className={isDark ? "text-gray-300" : "text-gray-500"} />
                  <input
                    type="date"
                    value={licenseDateISO}
                    onChange={(e) => setLicenseDateISO(e.target.value)}
                    className={inputBase}
                  />
                </div>
                <div className="text-xs opacity-70 mt-2">
                  *Using input type="date" (YYYY-MM-DD). Server will store as datetime.
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-3 mt-6">
              <button onClick={fetchDetails} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleSave} className={primaryBtn} disabled={loading}>
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default LicenseDateUpdate;